import React, {
  FC, useState, useEffect,
} from 'react';
import QuestionRenderer from '../../assessment-portal/components/question-renderer';
import PortalHeader from '../../assessment-portal/components/portal-header';
import QuestionnaireLoadingState from '../../assessment-portal/components/questionnaire-loading-state';
import QuestionnaireErrorState from '../../assessment-portal/components/questionnaire-error-state';
import QuestionnaireProgressHeader from '../../assessment-portal/components/questionnaire-progress-header';
import useQuestionnaireTemplate from '../../assessment-portal/hooks/use-questionnaire-template';
import useQuestionnaireNavigation from '../../assessment-portal/hooks/use-questionnaire-navigation';

interface AssessmentQuestionnairePageState {
  answers: Record<string, unknown>;
  questionIndex: number;
}

interface AssessmentQuestionnairePageProps {
  templateId: string;
  templateVersion: number;
  defaultAnswers: Record<string, unknown>;
  onBack: () => void;
  onAnswersChange?: (answers: Record<string, unknown>) => void;
  // eslint-disable-next-line no-unused-vars
  onNext: (meta: any, answers: Record<string, unknown>) => void;
  initialQuestionIndex?: number;
  onStateChange?: (state: AssessmentQuestionnairePageState) => void;
}

const AssessmentQuestionnairePage: FC<AssessmentQuestionnairePageProps> = ({
  templateId,
  templateVersion,
  defaultAnswers,
  onBack,
  onAnswersChange,
  onNext,
  initialQuestionIndex,
  onStateChange,
}) => {
  const {
    template,
    isLoading,
    error,
    flatQuestions,
    handleLoadTemplate,
  } = useQuestionnaireTemplate(templateId, templateVersion);

  const [answers, setAnswers] = useState<Record<string, any>>(
    defaultAnswers || {},
  );

  const {
    questionIndex,
    totalQuestions,
    currentQuestion,
    sectionColor,
    isVisible,
    cardRef,
    isManualAdvanceType,
    isCurrentAnswered,
    questionHint,
    handleGoNext,
    handleGoPrev,
  } = useQuestionnaireNavigation({
    flatQuestions,
    answers,
    onComplete: onNext,
    onBack,
    template,
    initialQuestionIndex,
  });

  useEffect(() => {
    handleLoadTemplate();
  }, [handleLoadTemplate]);

  useEffect(() => {
    onAnswersChange?.(answers);
  }, [answers, onAnswersChange]);

  useEffect(() => {
    onStateChange?.({ answers, questionIndex });
  }, [answers, questionIndex, onStateChange]);

  const totalParts = template?.parts.length || 0;

  if (isLoading) {
    return <QuestionnaireLoadingState />;
  }

  if (error || !template) {
    return (
      <QuestionnaireErrorState
        error={error || ''}
        onBack={onBack}
        onRetry={handleLoadTemplate}
      />
    );
  }

  if (!currentQuestion) return null;

  const partLabels = template.parts.map((p) => p.title);

  return (
    <div className="adhd-portal min-h-screen flex flex-col bg-page">
      <PortalHeader />
      <QuestionnaireProgressHeader
        currentQuestion={currentQuestion}
        totalParts={totalParts}
        questionIndex={questionIndex}
        totalQuestions={totalQuestions}
        sectionColor={sectionColor}
        partLabels={partLabels}
        parts={template.parts}
      />

      <div className="flex-1 px-4 py-8">
        <div className="max-w-content mx-auto">
          {currentQuestion.localIdx === 0 && currentQuestion.secInstructions && (
            <div
              className="rounded-xl p-3 mb-4 border text-xs leading-relaxed text-brand-mid"
              style={{
                background: `${sectionColor}08`,
                borderColor: `${sectionColor}30`,
              }}
            >
              <strong>Instructions:</strong>
              {' '}
              {currentQuestion.secInstructions}
            </div>
          )}

          <div
            ref={cardRef}
            className={`bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8 transition-all duration-200 ${
              isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-2'
            }`}
          >
            <div className="flex items-center gap-2 mb-5">
              <span
                className="text-xs font-bold px-2.5 py-1 rounded-full text-white"
                style={{ background: sectionColor }}
              >
                Q
                {currentQuestion.localIdx + 1}
                /
                {currentQuestion.localTotal}
              </span>
              {questionHint && (
                <span className="text-xs text-slate-400">
                  {questionHint}
                </span>
              )}
            </div>

            <QuestionRenderer
              key={currentQuestion.id}
              question={currentQuestion}
              answers={answers}
              setAnswers={setAnswers}
              onComplete={handleGoNext}
            />

            <div className="flex justify-between items-center mt-8 pt-5 border-t border-slate-100">
              <button
                type="button"
                onClick={handleGoPrev}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 cursor-pointer"
              >
                ← Back
              </button>
              {(isManualAdvanceType || isCurrentAnswered) && (
                <button
                  type="button"
                  onClick={handleGoNext}
                  disabled={!isCurrentAnswered}
                  className={`px-6 py-2.5 rounded-xl font-semibold text-sm text-white transition-all ${
                    isCurrentAnswered
                      ? 'bg-brand shadow-brand-sm cursor-pointer'
                      : 'bg-slate-300 cursor-not-allowed'
                  }`}
                >
                  {questionIndex === totalQuestions - 1 ? 'Finish →' : 'Next →'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentQuestionnairePage;
